import { showConfirm } from 'app/confirm/actions';
import { blockHistory, unblockHistory } from 'app/history';
import { getRouter } from 'app/selectors';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const CONFIRM_MESSAGE = 'You have unsaved changes, are you sure you want to leave?';

export type DirtyBlockHook = {
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
};

export type UseDirtyBlockParams = {
  exemptPaths: RegExp[];
  pathOnly: boolean;
  onConfirm?: () => void;
};

export const defaultParams: UseDirtyBlockParams = {
  exemptPaths: [],
  pathOnly: false
};

/**
 * This hook blocks changes to the page location when the `dirty` state is true.
 * It blocks changes in both the browser history and in `redux-little-router`.
 * This is used to prevent users from losing work before they can "clean" the
 * state by saving any changes.
 * @returns A function for setting the "dirty" state
 */
export const DIRTY_BLOCK_ID = 'useDirtyBlock';
export const DIRTY_BLOCK_PRIORITY = 1;

export function useDirtyBlock(paramsArg: Partial<UseDirtyBlockParams> = {}): DirtyBlockHook {
  const { pathOnly, exemptPaths, onConfirm } = {
    ...defaultParams,
    ...paramsArg
  };
  const dispatch = useDispatch();
  const [dirty, setDirty] = useState(false);
  const router = useSelector(getRouter);

  const handleOnBeforeUnload = useCallback(
    (e: BeforeUnloadEvent): string | undefined => {
      if (dirty) {
        e.returnValue = CONFIRM_MESSAGE;
        return CONFIRM_MESSAGE;
      }
    },
    [dirty]
  );

  const setBlock = useCallback(() => {
    window.addEventListener('beforeunload', handleOnBeforeUnload);
    blockHistory({
      priority: DIRTY_BLOCK_PRIORITY,
      id: DIRTY_BLOCK_ID,
      callback({ location, retry }) {
        const pathBlocked = !pathOnly || router.pathname !== location.pathname;
        const pathExempt = exemptPaths.some((exemptPath) => exemptPath.test(location.pathname ?? ''));
        if (pathBlocked && !pathExempt) {
          dispatch(
            showConfirm({
              title: 'Unsaved Changes',
              message: CONFIRM_MESSAGE,
              onConfirm() {
                onConfirm?.();
                unblockHistory(DIRTY_BLOCK_ID);
                retry();
              }
            })
          );
          return true;
        }

        return false;
      }
    });
  }, [handleOnBeforeUnload, pathOnly, router.pathname, exemptPaths, onConfirm, dispatch]);

  const releaseBlock = useCallback(() => {
    unblockHistory(DIRTY_BLOCK_ID);
    window.removeEventListener('beforeunload', handleOnBeforeUnload);
  }, [handleOnBeforeUnload]);

  useEffect(() => {
    if (dirty) {
      setBlock();
    } else {
      releaseBlock();
    }

    return releaseBlock;
  }, [dirty, setBlock, releaseBlock]);
  return { dirty, setDirty };
}
